import React, {useCallback, useEffect, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import usePageTitle from '../../hooks/usePageTitle';
import {axiosWithoutInterceptors} from "../../routes/axiosInstance";
import {ArrowLeftIcon, EyeIcon, EyeSlashIcon} from "@heroicons/react/24/outline";
import PublicNav from '../../components/PublicNav';
import PublicFooter from '../../components/PublicFooter';
import FormInput from '../../components/FormInput';
import {fetchInviteIsForecaster, fetchLegalDocument, LEGAL_PATHS} from '../../utils/legal';

const legalLinkClass = 'font-medium text-teal-700 dark:text-teal-400 hover:underline';

export default function SignUp() {
    usePageTitle('Create account');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [repeatPassword, setRepeatPassword] = useState('');
    const [error, setError] = useState('');
    const [token, setToken] = useState('');
    const [passwordsMatch, setPasswordsMatch] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showRepeatPassword, setShowRepeatPassword] = useState(false);

    // Only forecaster invites accept the Terms of Use and Privacy Policy here;
    // market owners are bound by their contracts. null = still checking.
    const [isForecasterInvite, setIsForecasterInvite] = useState(null);
    const [inviteError, setInviteError] = useState('');
    const [legalVersions, setLegalVersions] = useState(null);
    const [acceptedLegal, setAcceptedLegal] = useState(false);

    const [passwordValidation, setPasswordValidation] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        specialChar: false,
    });

    const navigate = useNavigate();
    const {token: inviteToken} = useParams();

    useEffect(() => {
        if (inviteToken) {
            setToken(inviteToken);
        }
    }, [inviteToken]);

    useEffect(() => {
        if (!inviteToken) return;
        fetchInviteIsForecaster(inviteToken)
            .then(setIsForecasterInvite)
            // 403 is the API's answer for an unknown, used or expired invite;
            // anything else (network, 5xx, throttling) is not the link's fault.
            .catch((err) => setInviteError(err?.response?.status === 403
                ? 'This invitation link is invalid or has expired.'
                : 'Your invitation could not be verified. Please reload the page.'));
    }, [inviteToken]);

    // The versions shown here are the ones recorded, so a text updated while
    // this page was open is rejected (409) instead of silently accepted.
    const loadLegalVersions = useCallback(() => {
        setLegalVersions(null);
        Promise.all([fetchLegalDocument('terms'), fetchLegalDocument('privacy')])
            .then(([terms, privacy]) => setLegalVersions({terms: terms.version, privacy: privacy.version}))
            .catch(() => setError('The Terms of Use could not be loaded. Please reload the page.'));
    }, []);

    useEffect(() => {
        if (isForecasterInvite) loadLegalVersions();
    }, [isForecasterInvite, loadLegalVersions]);

    useEffect(() => {
        if (repeatPassword) {
            setPasswordsMatch(password === repeatPassword);
        }
    }, [password, repeatPassword]);

    useEffect(() => {
        const validations = {
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /\d/.test(password),
            specialChar: /[@$!%*?&#]/.test(password),
        };
        setPasswordValidation(validations);
    }, [password]);

    const handleSignup = async (e) => {
        e.preventDefault();

        if (!Object.values(passwordValidation).every((isValid) => isValid)) {
            setError('Password does not meet the required criteria.');
            return;
        }

        if (isForecasterInvite && !acceptedLegal) {
            setError('Please accept the Terms of Use to register.');
            return;
        }
        // Equality check of two user-typed fields; no secret involved.
        // nosemgrep: eslint.detect-possible-timing-attacks, nodejs_scan.javascript-crypto-rule-node_timing_attack
        if (password !== repeatPassword) {
            setError('Passwords do not match.');
            return;
        }

        const headers = {
            headers: {Authorization: `Bearer ${token}`},
        };
        const payload = {
            email: email,
            password: password,
            first_name: firstName,
            last_name: lastName,
        };
        if (isForecasterInvite) payload.accepted_versions = legalVersions;

        try {
            const response = await axiosWithoutInterceptors.post('/user/register', payload, headers);

            if (response.status === 201) {
                navigate('/welcome');
            }
        } catch (error) {
            if (error.response?.status === 409) {
                // The Terms of Use or Privacy Policy changed while this page was open.
                setError('The Terms of Use or Privacy Policy were updated. Please review them and accept again.');
                setAcceptedLegal(false);
                loadLegalVersions();
                return;
            }

            // Parse validation errors from API response
            const responseData = error.response?.data || {};
            const validationErrors = responseData?.data || responseData;

            // Check for field-specific validation errors
            if (validationErrors && typeof validationErrors === 'object' && Object.keys(validationErrors).length > 0) {
                const errorFields = Object.entries(validationErrors).filter(([key, value]) =>
                    (Array.isArray(value) || typeof value === 'string') && !['message', 'status'].includes(key)
                );

                if (errorFields.length > 0) {
                    const errorMessage = errorFields
                        .map(([field, messages]) => {
                            const msgArray = Array.isArray(messages) ? messages : [messages];
                            return msgArray.join('. ');
                        })
                        .join('. ');
                    setError(errorMessage);
                    return;
                }
            }

            // Fallback error handling
            if (error.response && error.response.data.message) {
                setError(error.response.data.message);
            } else if (error.request) {
                setError('No response from the server. Please check your connection and try again.');
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
        }
    };

    const formBlocked = Boolean(inviteError) || isForecasterInvite === null || (isForecasterInvite && !legalVersions);

    return (
      <div className="min-h-screen bg-surface dark:bg-gray-870 text-slate-900 dark:text-gray-100 flex flex-col">
        <PublicNav/>

        <main className="flex-1 flex items-center justify-center w-full">
          <div className="max-w-md mx-auto px-6 py-16 w-full">
            <div className="text-center">
              <h1 className="text-xl sm:text-2xl font-bold leading-tight tracking-tight text-slate-900 dark:text-white">
                Create your <span className="italic text-teal-700 dark:text-teal-400">account</span>.
              </h1>
              <p className="mt-3 text-sm text-slate-600 dark:text-gray-300">
                Sign up to access the Predico dashboard.
              </p>
              <p className="mt-1 text-xs text-slate-500 dark:text-gray-400">
                Please provide your details to register.
              </p>
            </div>

            <div className="mt-10 rounded-2xl bg-surface dark:bg-gray-870 border border-slate-900/10 dark:border-gray-100/10 p-6 sm:p-8">
              <form onSubmit={handleSignup} className="space-y-6">
                {/* First Name */}
                <div>
                  <label htmlFor="first-name" className="block text-sm font-medium text-slate-700 dark:text-gray-300">First Name</label>
                  <FormInput id="first-name" name="first-name" type="text" variant="glass" required value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                </div>

                {/* Last Name */}
                <div>
                  <label htmlFor="last-name" className="block text-sm font-medium text-slate-700 dark:text-gray-300">Last Name</label>
                  <FormInput id="last-name" name="last-name" type="text" variant="glass" required value={lastName} onChange={(e) => setLastName(e.target.value)} />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-gray-300">Email address</label>
                  <FormInput id="email" name="email" type="email" variant="glass" required value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>

                {/* Password */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-slate-700 dark:text-gray-300">Password</label>
                  <div className="relative">
                    <FormInput id="password" name="password" type={showPassword ? 'text' : 'password'} variant="glass" required value={password} onChange={(e) => setPassword(e.target.value)} className="pr-10" />
                    <button type="button" className="absolute inset-y-0 right-0 flex items-center pr-3" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Repeat Password */}
                <div>
                  <label htmlFor="repeat-password" className="block text-sm font-medium text-slate-700 dark:text-gray-300">Repeat Password</label>
                  <div className="relative">
                    <FormInput id="repeat-password" name="repeat-password" type={showRepeatPassword ? 'text' : 'password'} variant="glass" required value={repeatPassword} onChange={(e) => setRepeatPassword(e.target.value)} className="pr-10" />
                    <button type="button" className="absolute inset-y-0 right-0 flex items-center pr-3" onClick={() => setShowRepeatPassword(!showRepeatPassword)} aria-label={showRepeatPassword ? 'Hide password' : 'Show password'}>
                      {showRepeatPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                  {repeatPassword && (
                    <span className={`text-sm ${passwordsMatch ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
                    </span>
                  )}
                </div>

                {/* Password Requirements */}
                <div className="text-sm text-slate-600 dark:text-gray-300">
                  <p>Your password must meet the following requirements:</p>
                  <ul className="list-disc list-inside">
                    <li className={passwordValidation.length ? 'text-green-600 dark:text-green-400' : 'text-slate-500 dark:text-gray-400'}>At least 8 characters long</li>
                    <li className={passwordValidation.uppercase ? 'text-green-600 dark:text-green-400' : 'text-slate-500 dark:text-gray-400'}>Includes an uppercase letter</li>
                    <li className={passwordValidation.lowercase ? 'text-green-600 dark:text-green-400' : 'text-slate-500 dark:text-gray-400'}>Includes a lowercase letter</li>
                    <li className={passwordValidation.number ? 'text-green-600 dark:text-green-400' : 'text-slate-500 dark:text-gray-400'}>Includes a number</li>
                    <li className={passwordValidation.specialChar ? 'text-green-600 dark:text-green-400' : 'text-slate-500 dark:text-gray-400'}>Includes a special character</li>
                  </ul>
                </div>

                {/* Error Message */}
                {inviteError && <p role="alert" className="text-red-600 dark:text-red-400 text-sm">{inviteError}</p>}
                {error && <p role="alert" aria-live="assertive" className="text-red-600 dark:text-red-400 text-sm">{error}</p>}

                {/* Terms of Use and Privacy Policy */}
                {isForecasterInvite && (
                  <div className="flex items-start">
                    <input type="checkbox" id="legal" name="legal" checked={acceptedLegal} onChange={() => setAcceptedLegal(!acceptedLegal)} required
                           className="mt-0.5 h-4 w-4 rounded border border-slate-300 dark:border-gray-600 text-teal-700 focus:ring-teal-600 bg-surface dark:bg-gray-870" />
                    <label htmlFor="legal" className="ml-3 text-sm text-slate-700 dark:text-gray-300">
                      I agree to the{' '}
                      <a href={LEGAL_PATHS.terms} target="_blank" rel="noopener noreferrer" className={legalLinkClass}>Terms of Use</a>
                      {' '}and have read the{' '}
                      <a href={LEGAL_PATHS.privacy} target="_blank" rel="noopener noreferrer" className={legalLinkClass}>Privacy Policy</a>
                    </label>
                  </div>
                )}
                {isForecasterInvite === false && (
                  <p className="text-xs text-slate-500 dark:text-gray-400">
                    Your personal data is processed as described in our{' '}
                    <a href={LEGAL_PATHS.privacy} target="_blank" rel="noopener noreferrer" className={legalLinkClass}>Privacy Policy</a>.
                  </p>
                )}

                <button type="submit" disabled={formBlocked} className="flex w-full justify-center items-center rounded-md bg-slate-900 text-white dark:bg-white dark:text-gray-900 px-4 py-2 text-sm font-medium h-10 shadow hover:opacity-90 transition focus:outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-white disabled:opacity-60 disabled:cursor-not-allowed">
                  Sign up
                </button>
              </form>
            </div>

            <div className="mt-6 text-center">
              <Link to="/"
                    className="inline-flex items-center gap-1.5 text-sm text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white transition">
                <ArrowLeftIcon className="h-4 w-4"/>
                Back to homepage
              </Link>
            </div>
          </div>
        </main>

        <PublicFooter/>
      </div>
    );
}
